(ns frontend.view.nev.fof-asset.view
  (:require
   [frontend.actions.common :as common-actions]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [common.localization :as loc]
   [clojure.string :as string]
   [frontend.logging :as logging]
   [frontend.view.nev.events :as events]
   [common.entity :as entity]
   [frontend.view.entity-viewers.entity-editor :as entity-editor]
   [frontend.view.not-found :as not-found]
   [frontend.routing :as routing]
   [frontend.view.entity-viewers.event-pane :as event-pane]
   [frontend.view.entity-viewers.header :as header]
   [frontend.view.entity-viewers.modules :as modules]
   [frontend.state.current-session :as session]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.view.entity-viewers.utils :as entity-utils]
   [frontend.view.field-templates :as templates]
   [frontend.view.syncing-data :as syncing]
   [frontend.db :as app-db]
   [frontend.view.entity-viewers.components :as nev-components]
   [common.intelligence.common :as ic]
   [frontend.view.entity-viewers.modules.associated-entities :as associated-entities]
   [frontend.utils :as fu]
   [common.schema.field :as field]
   [frontend.state.permissions :as f-permissions]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.view.entity-viewers.modules.risk.view :as risk-module]
   [frontend.view.entity-viewers.modules.risk.event :as risk-event]
   [frontend.view.modals.entity.attributes :as attrs]
   [frontend.tenant-customizations :as tenant-customizations]))

(defn toolbar-buttons
  [{:keys [entity] :as _view-args}]
  (let [entity-id (:db/id entity)
        flagged? (contains? @session/flagged-entity-ids
                            entity-id)
        entity-graph-feature? @(r/track session/entity-graph?)]
    (concat
     [(zap-toolbar/dropdown-button
       {:icon      :fa-star
        :on-select (partial common-actions/toggle-flag entity flagged?)
        :ord       1
        :label     (loc/tr (if flagged? :unflag-entity-menu-item :flag-entity-menu-item))})]
     (when-not (:common/disabled? entity)
       [(zap-toolbar/delete-button entity [:fof-assets-grid])])
     (when entity-graph-feature?
       [{:icon :fa-diagram-project
         :toolbar-button?  false
         :dropdown-action? true
         :primary-button?  false
         :on-select (fn [_]
                      (routing/set-view! [:entity-graph :id entity-id]))
         :ord -1
         :label (loc/tr :entity-graph)}])
     [(nev-components/audit-trail-button {:entity     entity
                                          :entity-ids [entity-id]})])))

(def target-types
  (concat (when-not (session/is-guest?)
            [:entity.type/person
             :entity.type/fof-asset])
          (when (session/portfolio?)
            [:entity.type/holding])))

(defn header-top
  [{:keys [entity read-only?]}]
  [:div
   {:class "flex gap-2"}
   [:span.entity-logo-container.icon-container
    {:style {:padding-left "0"}}
    [:i
     {:class-name (fu/cs :fal (entity/default-icon entity))}]]
   [:div.nev-name-row-container
    [nev-components/name-view
     {:entity     entity
      :read-only? read-only?}]]])

(def default-field-defs
  [{:fields       {0 {:field :asset/description}}
    :field-layout :f-4}])

(defn header-board
  [{:keys [_entity-atom]}]
  (let [header-state (r/atom {:edit-layout? false})
        set-edit-layout! (fn [val] (swap! header-state assoc :edit-layout? val))]
    (fn [{:keys [entity-atom]}]
      (let [edit-layout? (:edit-layout? @header-state)
            entity @entity-atom
            entity-id (:db/id entity)
            field-defs-path [:fof-asset :header :field-defs]
            field-defs-timeout-id :fof-asset-field-defs
            field-defs (or (events/timeout-value field-defs-timeout-id
                                                 @(r/track session/get-view-config field-defs-path))
                           default-field-defs)
            overrides nil
            selected-fields (->> field-defs
                                 (map :fields)
                                 (mapcat vals)
                                 (keep :field))
            remove-fields #{}
            schema (templates/entity-type-template
                    {:entity-type     :entity.type/fof-asset
                     :selected-fields selected-fields
                     :tenant-schema   @session/tenant-schema
                     :account         @session/account
                     :role            @session/role
                     :remove-fields   remove-fields
                     :overrides       overrides})

            ;;test all fields:
            #_#_field-defs
              (->> schema
                   (map first)
                   (partition-all 4 4)
                   (map (fn [fields]
                          (->> fields
                               (map-indexed (fn [i f]
                                              [i {:field f}])))))
                   (map (partial into {}))
                   (map (fn [fields]
                          {:fields       fields
                           :field-layout :f-1-1-1-1}))
                   vec)]
        [:div
         [header/header-board
          {:top              [header-top {:entity entity}]
           :preview-top      [header-top
                              {:entity     entity
                               :read-only? true}]
           :actions          (toolbar-buttons {:entity entity})
           :entity-id        entity-id
           :entity-atom      entity-atom
           :edit-layout?     edit-layout?
           :set-edit-layout! set-edit-layout!
           :field-defs       field-defs
           :schema           schema
           :save-layout!     (fn [new-field-defs]
                               (events/save-field-defs! field-defs-timeout-id
                                                        field-defs-path
                                                        new-field-defs
                                                        (fn [])
                                                        (fn [err]
                                                          (logging/error
                                                           "Failed to save field layout"
                                                           err))))}]]))))

(defn new-header?
  []
  (and (session/has-feature-access? :feature/customizable-nev)
       @session/customizable-nev?))

(defn fof-asset-view
  [{:keys [fof-asset-id is-guest?] :as _view-args}]
  (let [state-atom (r/cursor app-db/app-state [:views :nev :fof-asset])
        initialized? (r/atom false)
        temp-atom (r/cursor state-atom [:temp])
        view-state-atom (r/cursor state-atom [:view-state])
        error-atom (r/cursor state-atom [:error])
        notification-atom (r/cursor state-atom [:notification])
        events-pane-atom (r/cursor state-atom [:events-pane])
        entity-atom (r/cursor session/entities [fof-asset-id])]
    (r/create-class
     {:component-did-update
      (partial entity-utils/component-did-update
               :fof-asset-id
               (partial events/clear-errors! error-atom notification-atom))
      :component-did-mount
      (fn [_]
        (entity-utils/reset-state! state-atom fof-asset-id
                                   {:always-fetch? (session/new-grid-2023?)})
        (reset! initialized? true)
        (when (and (session/risks-access?)
                   (f-permissions/field-read-access? :fof-asset/risk-entities
                                                     :entity.type/fof-asset
                                                     nil))
          (rf/dispatch [::risk-event/load-risks fof-asset-id])))
      :component-will-unmount
      (fn [_]
        (events/clear-errors! error-atom notification-atom)
        (entity-utils/clear-state! state-atom))
      :reagent-render
      (fn [{:keys [fof-asset-id] :as view-args}]
        (let [fof-asset (get @session/entities fof-asset-id)
              enriched-entity (ic/enrich-entity fof-asset)]
          (cond
            (not @initialized?)
            [syncing/view]
            (or (:common/disabled? fof-asset)
                (:not-found @state-atom))
            [not-found/not-found-content]
            (:loading @state-atom)
            [syncing/view]
            :else
            (if (or (not fof-asset)
                    (:common/disabled? fof-asset))
              [:div " "]
              (let [tenant-schema @session/tenant-schema
                    oppo-entities-where-target-asset
                    (when (session/dealflow?)
                      (->> @session/all-oppos
                           (filter (fn [o]
                                     ;; TODO: @pirinen - filter based on access to pipeline where deal is ?
                                     (when-not (:common/disabled? o)
                                       (= (:oppo/target-asset o) fof-asset-id))))))
                    holding-entities-where-target-asset
                    (when (session/portfolio?)
                      (->> @session/all-holdings
                           (filter (fn [h]
                                     ;; TODO: @pirinen - filter based on something else ???
                                     (when-not (:common/disabled? h)
                                       (= (:holding/target-asset h) fof-asset-id))))))
                    #_#_existing-guests (->> @session/all-roles
                                             (cu/only-entity :role/role :role/guest-sell-side)
                                             (remove :common/disabled?)
                                             (cu/only-entity :role/target fof-asset-id))
                    header-fields [:asset/description]
                    view-id :fof-asset
                    module-collapsed-path (fn [module-id]
                                            [view-id :modules :module-config module-id :collapsed?])
                    toolbar-back-to-label (loc/tr :navbar-item/third-party-funds)
                    eq-varainhoito? (tenant-customizations/eq-varainhoito?)]
                [:<>
                 (when-not (session/is-guest?)
                   [zap-toolbar/nev-toolbar
                    {:state           state-atom
                     :label           (partial zap-toolbar/toolbar-back
                                               [:fof-assets-grid]
                                               toolbar-back-to-label)
                     :actions         (toolbar-buttons (assoc view-args
                                                              :entity
                                                              fof-asset))
                     :dropdown-toggle [:span (loc/tr :actions)
                                       [:i.fas.fa-angle-down.ml-2]]
                     :dropdown-toggle-class-name
                     "zap-button zap-button--primary-btn actions-dropdown"}])
                 [:div.entity__container
                  (when @error-atom
                    [nev-components/error-bar error-atom])
                  (when @notification-atom
                    [nev-components/notification-bar notification-atom])

                  [:div.entity
                   [:div
                    {:class "w-full max-w-[1920px] mx-auto p-4"}
                    (if (new-header?)
                      [header-board {:entity-atom entity-atom}]
                      [nev-components/header-module
                       {:component-state temp-atom
                        :view-state      view-state-atom
                        :entity          fof-asset
                        :enriched-entity enriched-entity
                        :entity-atom     entity-atom
                        :selected-fields header-fields}])
                    [:div.md:grid.grid-cols-10.gap-4
                     [:div
                      {:class (if (session/event-pane-disabled?)
                                "col-span-10"
                                "col-span-10 lg:col-span-5")}
                      [:div.entity-left
                       [modules/view
                        {:view-id  view-id
                         :ordering (get-in @session/view-config [view-id :modules :ordering])
                         :save-modules-order! (partial events/save-modules-order!
                                                       (:entity/type fof-asset))
                         :modules
                         (concat
                          [{:module-id  :fof-asset-information
                            :header     (loc/tr :nev-module/about-this-entity-type
                                                [(string/lower-case (loc/tr (:entity/type fof-asset)))])
                            ;;TODO uncomment when finished:
                            #_:header-tools
                            #_(when (session/is-admin?)
                                [:span.entity-editor-header-settings
                                 {:on-click (fn [e]
                                                ;;open settings
                                              )}
                                 [:span (loc/tr :entity-editor/settings-label)]
                                 [:i.fal.fa-cog]])
                            :collapsed? (get-in @session/view-config
                                                (module-collapsed-path :fof-asset-information))
                            :save-module-collapsed! (partial events/save-module-collapsed!
                                                             (:entity/type fof-asset))
                            :content    [:div
                                         [entity-editor/view
                                          {:state-atom    state-atom
                                           :entity-id     fof-asset-id
                                           :view-id       view-id
                                           :module-id     :fof-asset-information
                                           :remove-fields (concat [:asset/name
                                                                   :fof-asset/gp
                                                                   :common/network-drive :common/named-network-drives]
                                                                  (field/entity-tag-fields
                                                                   tenant-schema
                                                                   (:entity/type fof-asset))
                                                                  header-fields)
                                           :ordering      (cond eq-varainhoito?
                                                                attrs/eq-fof-asset-attrs
                                                                :else
                                                                (get-in @session/view-config
                                                                        [view-id :modules :module-config
                                                                         :fof-asset-information :ordering]))}]]}

                           (when (and (or (:fof-asset/gp fof-asset)
                                          (not is-guest?))
                                      (f-permissions/field-read-access? :fof-asset/gp
                                                                        :entity.type/fof-asset
                                                                        nil))
                             (let [module-id :fof-asset-gp
                                   fof-asset-gp (:fof-asset/gp fof-asset)
                                   order-config-path [view-id :modules :module-config module-id :entity.type/company :ordering]
                                   collapsed-path (module-collapsed-path module-id)
                                   collapsed? (get-in @session/view-config collapsed-path)]
                               (nev-components/entity-relation-module
                                (merge
                                 {:state-atom          state-atom
                                  :module-id           module-id
                                  :view-id             view-id
                                  :ordering            (get-in
                                                        @session/view-config
                                                        order-config-path)
                                  :order-config-path   order-config-path
                                  :module-label        (loc/tr :nev-module/fof-asset-gp)
                                  :entity              fof-asset
                                  :forced-entity-types [:entity.type/company]
                                  :pipeline-id         nil
                                  :relationship-attribute :fof-asset/gp
                                  :header-tools
                                  [:span.grow.flex.justify-end
                                   (when (and (not is-guest?)
                                              fof-asset-gp
                                              (not collapsed?)
                                              (not (session/is-read-only-user?)))
                                     [nev-components/remove-attribute-icon
                                      {:entity    fof-asset
                                       :attribute :fof-asset/gp}])]}))))

                           (when (and (session/risks-access?)
                                      (f-permissions/field-read-access? :fof-asset/risk-entities
                                                                        :entity.type/fof-asset
                                                                        nil))
                             (risk-module/risk-module-config
                              {:module-label    (loc/tr :nev-module/fof-asset-risks)
                               :module-id       :holding-risks
                               :view-id         view-id
                               :nev-entity-type :entity.type/fof-asset
                               :save-collapsed-status! (r/partial events/save-module-collapsed!
                                                                  (:entity/type
                                                                   :entity.type/holding))
                               :entity-id       fof-asset-id
                               :add-new-button? false}))

                           (when (and (not (session/is-guest?))
                                      (session/dealflow?))

                             (nev-components/association-module-config
                              {:module-label        (loc/tr :nev-module/fof-asset-target-asset-in-oppos)
                               :module-id           :target-asset-in-oppos
                               :view-id             view-id
                               :nev-entity-type     :entity.type/fof-asset
                               :target-entity-type  :entity.type/oppo
                               :associated-entities oppo-entities-where-target-asset
                               :save-collapsed-status! (partial events/save-module-collapsed!
                                                                (:entity/type fof-asset))
                               :new-entity-start-value {:oppo/target-asset (:db/id fof-asset)
                                                        :oppo/name         (:asset/name fof-asset)}
                               :add-new-button?     true
                               :add-new-content
                               (when (session/deal-creation-flow?)
                                 [nev-components/opportunity-creator-dropdown
                                  {:entity-type :entity.type/oppo
                                   :initial-target fof-asset}])}))
                           (when (and (not (session/is-guest?))
                                      (session/portfolio?))
                             (nev-components/association-module-config
                              {:module-label        (loc/tr :nev-module/target-asset-in-holdings)
                               :module-id           :target-asset-in-holdings
                               :view-id             view-id
                               :nev-entity-type     :entity.type/fof-asset
                               :target-entity-type  :entity.type/holding
                               :associated-entities holding-entities-where-target-asset
                               :save-collapsed-status! (partial events/save-module-collapsed!
                                                                (:entity/type fof-asset))
                               :new-entity-start-value {:holding/target-asset (:db/id fof-asset)
                                                        :holding/name         (:asset/name
                                                                               fof-asset)}
                               :add-new-button?     true}))]
                          (when-not (session/is-guest?)
                            (associated-entities/modules
                             {:entity-id fof-asset-id
                              :save-module-collapsed! events/save-module-collapsed!
                              :module-collapsed-path module-collapsed-path}))
                          [                           ;; removing for now:
                           #_(when (plan/available :feature/sell-side-guest
                                                   @session/tenant
                                                   @session/role)
                               {:module-id        :guest-accounts
                                :header           (loc/tr :nev-module/guest-accounts)
                                :collapsed?       (get-in @session/view-config
                                                          (module-collapsed-path :guest-accounts))
                                :save-module-collapsed! (partial events/save-module-collapsed!
                                                                 :entity.type/person)
                                :start-collapsed? (empty? existing-guests)
                                :content          [:div
                                                   [guests-module/view
                                                    {:entity-id          fof-asset-id
                                                     :fields             [{:attribute
                                                                           :role/guest-sell-side
                                                                           :label
                                                                           (loc/tr :sell-side-guests)}]
                                                     :show-notification! (partial
                                                                          events/show-notification!
                                                                          notification-atom)
                                                     :show-error!        (partial events/show-error!
                                                                                  error-atom)
                                                     :guest-roles        existing-guests}]]})

                           (when (and (not (session/is-mobile?))
                                      (f-permissions/field-read-access? :common/attachments
                                                                        :entity.type/fof-asset
                                                                        nil))
                             (nev-components/attachments-module view-id
                                                                :fof-asset-attachments
                                                                fof-asset))])}]]]
                     (when-not (session/event-pane-disabled?)
                       [:div
                        {:class "col-span-10 lg:col-span-5"}
                        [event-pane/view
                         {:state       events-pane-atom
                          :entity-ids  [fof-asset-id]
                          :module-args {:state-atom state-atom
                                        :view-id    view-id}}]])]]]]])))))})))

(defmethod routing/render-view :entity.type/fof-asset
  [{:keys [route-params]}]
  (let [fof-asset-id (-> route-params
                         :id
                         js/parseInt)]
    [default-page-layout/view
     {:class-name "zap-page fof-asset"}
     ;;the key change forces rerendering of the component (and its child components)
     ^{:key (str "fof-asset-viewer-id-" fof-asset-id)}
     [fof-asset-view {:fof-asset-id fof-asset-id}]]))
